/**
 * Centralized Authorization Configuration for HRM + CRM + ERP System
 *
 * This file defines:
 * - Roles
 * - Module access codes
 * - Feature access codes
 * - Central authorization mapping
 * - Helpers to query feature availability
 */

/**
 * ================================
 * MODULE + FEATURE ACCESS CODES (BACKEND DRIVEN)
 * These codes must match backend moduleAccessID + moduleFeatureAccessIDs.
 * ================================
 */
export const MODULES = {
  CRM: "CRM",
  ERP: "ERP",
  INVENTORY: "INVENTORY",
};

export const MODULE_FEATURES = {
  CRM: [
    "CRM_LEADS",
    "CRM_CLIENTS",
    "CRM_ENQUIRIES",
    "CRM_TEAM_ENQUIRY",
    "CRM_TEAM_MANAGE_LEADS",
    "CRM_TEAM_MANAGE_CLIENTS",
  ],

  ERP: ["ERP_TEAM_PROD_APPROVALS", "ERP_TEAM_PROD_MANAGER", "ERP_OPERATIONS"],
};

/**
 * ================================
 * ROLE DEFINITIONS (Your latest list)
 * ================================
 */
export const ROLES = {
  ADMIN: "ROLE_ADMIN",
  HR: "ROLE_HR",
  MANAGER: "ROLE_MANAGER",
  ACCOUNTANT: "ROLE_ACCOUNTANT",
  EMPLOYEE: "ROLE_EMPLOYEE",

  SALES: "ROLE_SALES", // matches your table
  PRODUCTION: "ROLE_PRODUCTION", // matches your table
  OPERATIONS: "ROLE_OPERATIONS", // ← add this

  CRM_ADMIN: "ROLE_CRM-ADMIN",
  INVENTORY_MANAGER: "ROLE_INVENTORY-MANAGER",
};

/**
 * ================================
 * CRM AUTH (Role-based CRM UI permissions)
 * ================================
 */
export const CRM_AUTH = {
  MANAGE_ENQUIRY_STAGES: {
    roles: [ROLES.CRM_ADMIN],
    feature: "CRM_ENQUIRY_STAGES",
    description: "Create/update/manage enquiry stages.",
  },

  CREATE_LEADS: {
    roles: [ROLES.SALES],
    feature: "CRM_LEADS",
  },
  ASSIGN_LEADS: {
    roles: [ROLES.CRM_ADMIN, ROLES.SALES],
    feature: "CRM_ASSIGN_LEADS",
  },

  VIEW_ALL_ENQUIRIES: {
    roles: [ROLES.SALES],
    feature: "CRM_ENQUIRIES",
  },
  VIEW_OWN_ENQUIRIES: {
    roles: [ROLES.SALES],
    feature: "CRM_ENQUIRIES",
  },

  ANALYTICS: {
    roles: [ROLES.CRM_ADMIN],
    feature: "CRM_ANALYTICS",
  },
};

/**
 * ================================
 * ERP AUTH (Role-based ERP UI permissions)
 * ================================
 */
export const ERP_AUTH = {
  MANAGE_OPERATION_STAGES: {
    roles: [ROLES.OPERATIONS], // ← was ROLES.PRODUCTION
    feature: "ERP_OPERATIONS",
  },
  MANAGE_OPERATION_CHECKLISTS: {
    roles: [ROLES.OPERATIONS], // ← was ROLES.PRODUCTION
    feature: "ERP_OPERATION_CHECKLISTS",
  },
  APPROVE_PURCHASE_ORDERS: {
    roles: [ROLES.OPERATIONS], // ← was ROLES.PRODUCTION
    feature: "ERP_APPROVE_PURCHASE_ORDERS",
  },
  VIEW_ALL_PRODUCTS_PROGRESS: {
    roles: [ROLES.OPERATIONS], // ← was ROLES.PRODUCTION
    feature: "ERP_VIEW_ALL_PRODUCTS_PROGRESS",
  },
  UPDATE_ORDER_STATUS: {
    roles: [ROLES.OPERATIONS], // ← was ROLES.PRODUCTION
    feature: "ERP_UPDATE_ORDER_STATUS",
  },
};

/**
 * ================================
 * INVENTORY AUTH (Role-based)
 * ================================
 */
export const INVENTORY_AUTH = {
  MANAGE_WAREHOUSES: {
    roles: [ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_WAREHOUSES",
  },
  MANAGE_SUPPLIERS: {
    roles: [ROLES.INVENTORY_MANAGER],
    feature: "INV_MANAGE_SUPPLIERS",
  },
  MANAGE_INVENTORY_ITEMS: {
    roles: [ROLES.INVENTORY_MANAGER, ROLES.CRM_ADMIN],
    feature: "INV_MANAGE_ITEMS",
  },
  UPDATE_STOCK: {
    roles: [ROLES.INVENTORY_MANAGER],
    feature: "INV_UPDATE_STOCK",
  },
};

/**
 * ================================
 * CONNECT ALL MODULE AUTH TOGETHER
 * ================================
 */
export const ALL_AUTH_CONFIGS = {
  CRM: CRM_AUTH,
  ERP: ERP_AUTH,
  INVENTORY: INVENTORY_AUTH,
};

/**
 * ================================
 * HELPERS
 * ================================
 */

/**
 * ROLE-BASED FEATURE LOOKUP
 */
export const getAuthConfig = (module, feature) => {
  const mod = module?.toUpperCase();
  const feat = feature?.toUpperCase();
  if (!mod || !feat) return null;

  const config = ALL_AUTH_CONFIGS[mod];
  if (!config) return null;

  return config[feat] || null;
};

/**
 * Get features available for a role
 */
export const getFeaturesForRole = (role) => {
  const list = [];

  Object.values(ALL_AUTH_CONFIGS).forEach((moduleConfig) => {
    Object.entries(moduleConfig).forEach(([featureKey, cfg]) => {
      if (cfg.roles?.includes(role)) {
        list.push(cfg.feature);
      }
    });
  });

  return list;
};

/**
 * Backend module-feature → needs upgrade?
 */
export const requiresUpgrade = (module, feature) => {
  const mod = module?.toUpperCase();
  const feat = feature?.toUpperCase();
  return MODULE_FEATURES[mod]?.includes(feat) ? true : false;
};

/**
 * Menu helpers
 */
export const generateAuthorizedMenuItems = (items, module) => {
  return items.map((item) => {
    if (!item.authKey) return item;
    return {
      ...item,
      auth: getAuthConfig(module, item.authKey),
    };
  });
};
